import { VERSION } from './constants.js';
import {
  parseConsentConfiguration,
} from './config-validator.js';
import type {
  ConsentConfigurationErrorCode,
} from './config-validator.js';
import { isCanonicalSdkVersion, isSdkVersionAtLeast } from './sdk-version.js';
import type { ConsentConfiguration } from './types.js';

export const CONFIG_FETCH_TIMEOUT_MS = 5_000;
export const MAX_CONFIG_RESPONSE_BYTES = 262_144;

export type FetchLike = (
  input: string | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface ConsentConfigFetchOptions {
  readonly siteId: string;
  readonly configUrl: string;
  readonly sdkVersion?: string;
  readonly timeoutMs?: number;
  readonly allowInsecureLocalhost?: boolean;
  readonly fetch?: FetchLike;
}

export type ConsentConfigFetchErrorCode =
  | 'http_error'
  | 'insecure_url'
  | 'invalid_config'
  | 'invalid_content_type'
  | 'invalid_json'
  | 'invalid_options'
  | 'invalid_url'
  | 'redirect_rejected'
  | 'request_failed'
  | 'response_too_large'
  | 'sdk_incompatible'
  | 'timeout';

export interface ConsentConfigFetchError {
  readonly code: ConsentConfigFetchErrorCode;
  readonly message: string;
  readonly status?: number;
  readonly configErrorCode?: ConsentConfigurationErrorCode;
}

export type ConsentConfigFetchResult =
  | {
      readonly ok: true;
      readonly config: ConsentConfiguration;
      readonly etag: string | null;
    }
  | { readonly ok: false; readonly error: ConsentConfigFetchError };

function failure(
  code: ConsentConfigFetchErrorCode,
  message: string,
  details: Pick<ConsentConfigFetchError, 'status' | 'configErrorCode'> = {},
): ConsentConfigFetchResult {
  return { ok: false, error: { code, message, ...details } };
}

function isLocalhost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]'
  );
}

function parseSecureConfigUrl(
  rawUrl: string,
  allowInsecureLocalhost: boolean,
): URL | ConsentConfigFetchResult {
  let url: URL;

  try {
    url = new URL(rawUrl);
  } catch {
    return failure('invalid_url', 'Config URL must be an absolute URL.');
  }

  if (url.username !== '' || url.password !== '') {
    return failure('invalid_url', 'Config URL must not contain credentials.');
  }

  if (url.protocol === 'https:') {
    return url;
  }

  if (
    url.protocol === 'http:' &&
    allowInsecureLocalhost &&
    isLocalhost(url.hostname)
  ) {
    return url;
  }

  return failure(
    'insecure_url',
    'Config URL must use HTTPS; HTTP is allowed only for explicit localhost development.',
  );
}

function isJsonContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  const mediaType = contentType.split(';', 1)[0]?.trim().toLowerCase() ?? '';
  return mediaType === 'application/json' || mediaType.endsWith('+json');
}

export async function fetchConsentConfiguration(
  options: ConsentConfigFetchOptions,
): Promise<ConsentConfigFetchResult> {
  const timeoutMs = options.timeoutMs ?? CONFIG_FETCH_TIMEOUT_MS;
  const sdkVersion = options.sdkVersion ?? VERSION;

  if (
    !Number.isSafeInteger(timeoutMs) ||
    timeoutMs < 1 ||
    timeoutMs > 60_000 ||
    !isCanonicalSdkVersion(sdkVersion)
  ) {
    return failure(
      'invalid_options',
      'Fetch timeout or current SDK version is invalid.',
    );
  }

  const parsedUrl = parseSecureConfigUrl(
    options.configUrl,
    options.allowInsecureLocalhost ?? false,
  );
  if (!(parsedUrl instanceof URL)) {
    return parsedUrl;
  }

  const fetchImplementation = options.fetch ?? globalThis.fetch;
  if (typeof fetchImplementation !== 'function') {
    return failure(
      'invalid_options',
      'A Fetch API implementation is required.',
    );
  }

  const controller = new AbortController();
  const startedAt = Date.now();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  let response: Response;
  try {
    response = await fetchImplementation(parsedUrl, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
    });
  } catch {
    return timedOut
      ? failure('timeout', 'Config request timed out.')
      : failure('request_failed', 'Config request failed.');
  } finally {
    clearTimeout(timeout);
  }

  if (timedOut) {
    return failure('timeout', 'Config request timed out.');
  }

  if (response.redirected) {
    return failure(
      'redirect_rejected',
      'Redirected config responses are not accepted.',
    );
  }

  if (!response.ok) {
    return failure('http_error', 'Config endpoint returned an error.', {
      status: response.status,
    });
  }

  if (!isJsonContentType(response.headers.get('content-type'))) {
    return failure(
      'invalid_content_type',
      'Config response must use a JSON content type.',
    );
  }

  const declaredLength = response.headers.get('content-length');
  if (
    declaredLength !== null &&
    /^\d+$/.test(declaredLength) &&
    Number(declaredLength) > MAX_CONFIG_RESPONSE_BYTES
  ) {
    return failure(
      'response_too_large',
      'Config response exceeds the allowed size.',
    );
  }

  let responseText: string;
  const remainingTimeoutMs = timeoutMs - (Date.now() - startedAt);
  if (remainingTimeoutMs <= 0) {
    return failure('timeout', 'Config request timed out.');
  }

  const bodyTimeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, remainingTimeoutMs);

  try {
    responseText = await response.text();
  } catch {
    return timedOut
      ? failure('timeout', 'Config request timed out while reading its body.')
      : failure('request_failed', 'Config response body could not be read.');
  } finally {
    clearTimeout(bodyTimeout);
  }

  if (new TextEncoder().encode(responseText).byteLength > MAX_CONFIG_RESPONSE_BYTES) {
    return failure(
      'response_too_large',
      'Config response exceeds the allowed size.',
    );
  }

  let rawConfig: unknown;
  try {
    rawConfig = JSON.parse(responseText);
  } catch {
    return failure('invalid_json', 'Config response is not valid JSON.');
  }

  const parsedConfig = parseConsentConfiguration(rawConfig, options.siteId);
  if (!parsedConfig.ok) {
    return failure('invalid_config', parsedConfig.message, {
      configErrorCode: parsedConfig.code,
    });
  }

  if (!isSdkVersionAtLeast(sdkVersion, parsedConfig.config.sdk.minVersion)) {
    return failure(
      'sdk_incompatible',
      'Current SDK version is below the config minimum version.',
    );
  }

  return {
    ok: true,
    config: parsedConfig.config,
    etag: response.headers.get('etag'),
  };
}
